export type DateOnlyLocale = "ru" | "en";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

export function parseDateOnly(value: string): Date | null {
  const match = DATE_ONLY_PATTERN.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function formatDateOnly(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

export function todayDateOnly(): string {
  return formatDateOnly(new Date());
}

export function addDateOnlyDays(value: string, amount: number): string {
  const date = parseDateOnly(value);
  if (!date) return value;
  date.setDate(date.getDate() + amount);
  return formatDateOnly(date);
}

export function addDateOnlyMonths(value: string, amount: number): string {
  const date = parseDateOnly(value);
  if (!date) return value;

  const targetMonth = date.getMonth() + amount;
  const targetYear = date.getFullYear() + Math.floor(targetMonth / 12);
  const normalizedMonth = ((targetMonth % 12) + 12) % 12;
  const lastDay = new Date(targetYear, normalizedMonth + 1, 0).getDate();
  return formatDateOnly(new Date(targetYear, normalizedMonth, Math.min(date.getDate(), lastDay)));
}

export function startOfDateOnlyWeek(value: string): string {
  const date = parseDateOnly(value);
  if (!date) return value;
  const mondayOffset = (date.getDay() + 6) % 7;
  return addDateOnlyDays(value, -mondayOffset);
}

export function buildDateOnlyMonth(viewMonth: string): string[] {
  const date = parseDateOnly(viewMonth);
  if (!date) return [];
  const first = formatDateOnly(new Date(date.getFullYear(), date.getMonth(), 1));
  const gridStart = startOfDateOnlyWeek(first);
  return Array.from({ length: 42 }, (_, index) => addDateOnlyDays(gridStart, index));
}

export function dateOnlyMonthStart(value: string): string {
  const date = parseDateOnly(value);
  if (!date) return value;
  return formatDateOnly(new Date(date.getFullYear(), date.getMonth(), 1));
}

export function formatDateOnlyDisplay(value: string, locale: DateOnlyLocale): string {
  const date = parseDateOnly(value);
  if (!date) return value;
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  return locale === "en" ? `${month}/${day}/${year}` : `${day}.${month}.${year}`;
}
